import * as React from 'react';

import { tv, type VariantProps } from 'tailwind-variants';

import cn from '../../utils/cn';

const tagVariants = tv({
  base: 'inline-block items-center whitespace-nowrap justify-center rounded-2xl w-auto bg-white text-xs font-medium border border-transparent',
  variants: {
    size: {
      condensed: 'px-2.5 py-1',
      default: 'px-2.5 py-2',
    },
    color: {
      default: 'text-gray-dark',
      primary: 'text-primary-dark',
      success: 'text-success-dark',
      danger: 'text-danger',
      warning: 'text-warning-dark',
      accessory2: 'text-accessory-2',
      accessory5: 'text-accessory-5',
      'danger-dark': 'text-danger-dark',
    },
    variant: {
      default: 'bg-gray-lightest text-gray-dark',
      contained: 'bg-gray-light',
      outlined: 'border border-gray-light',
      surface: '',
    },
  },
  compoundVariants: [
    //-=-=-== CONTAINED -=-=-=-=
    {
      variant: 'contained',
      color: 'default',
      className: 'bg-gray-dark text-white',
    },
    {
      variant: 'contained',
      color: 'primary',
      className: 'bg-primary text-white',
    },
    {
      variant: 'contained',
      color: 'success',
      className: 'bg-success text-white',
    },
    {
      variant: 'contained',
      color: 'danger',
      className: 'bg-danger text-white',
    },
    {
      variant: 'contained',
      color: 'warning',
      className: 'bg-warning text-white',
    },
    {
      variant: 'contained',
      color: 'danger-dark',
      className: 'bg-danger-dark text-white',
    },
    {
      variant: 'contained',
      color: 'accessory2',
      className: 'bg-accessory-2 text-white',
    },
    {
      variant: 'contained',
      color: 'accessory5',
      className: 'bg-accessory-5 text-white',
    },

    // -=-=-=-= SURFACE -=-=-=-=
    {
      variant: 'surface',
      color: 'default',
      className: 'bg-gray-lightest text-gray-dark',
    },
    {
      variant: 'surface',
      color: 'primary',
      className: 'bg-primary-lightness text-primary-dark',
    },
    {
      variant: 'surface',
      color: 'success',
      className: 'bg-success-lightness text-success-dark',
    },
    {
      variant: 'surface',
      color: 'danger',
      className: 'bg-danger-lightness text-danger',
    },
    {
      variant: 'surface',
      color: 'warning',
      className: 'bg-warning-lightness text-warning-dark',
    },
    {
      variant: 'surface',
      color: 'danger-dark',
      className: 'bg-danger-dark/10 text-danger-dark',
    },
    {
      variant: 'surface',
      color: 'accessory2',
      className: 'bg-accessory-2/10 text-accessory-2',
    },
    {
      variant: 'surface',
      color: 'accessory5',
      className: 'bg-accessory-5/10 text-accessory-5',
    },

    // -=-=-=-= OUTLINE -=-=-=-=
    {
      variant: 'outlined',
      color: 'default',
      className: 'border-gray-dark text-gray-dark',
    },
    {
      variant: 'outlined',
      color: 'primary',
      className: 'border-primary-dark text-primary-dark',
    },
    {
      variant: 'outlined',
      color: 'success',
      className: 'border-success-dark text-success-dark',
    },
    {
      variant: 'outlined',
      color: 'danger',
      className: 'border-danger text-danger',
    },
    {
      variant: 'outlined',
      color: 'warning',
      className: 'border-warning-dark text-warning-dark',
    },
    {
      variant: 'outlined',
      color: 'danger-dark',
      className: 'border-danger-dark text-danger-dark',
    },
    {
      variant: 'outlined',
      color: 'accessory2',
      className: 'border-accessory-2 text-accessory-2',
    },
    {
      variant: 'outlined',
      color: 'accessory5',
      className: 'border-accessory-5 text-accessory-5',
    },
  ],
  defaultVariants: {
    color: 'default',
    size: 'default',
    variant: 'default',
  },
});

const customIcon = (icon: React.ReactNode, color: DefaultProps['color'], variant: TagProps['variant']) => {
  return React.cloneElement(icon as React.ReactElement, {
    'data-testid': 'tag-icon',
    style: { width: '1rem', height: '1rem' }, // 12px
    className: cn(
      'fill-current',
      variant === 'default' && {
        'text-gray-dark': color === 'default',
        'text-primary-dark': color === 'primary',
        'text-success-dark': color === 'success',
        'text-danger': color === 'danger',
        'text-warning-dark': color === 'warning',
        'text-danger-dark': color === 'danger-dark',
        'text-accessory-2': color === 'accessory2',
        'text-accessory-5': color === 'accessory5',
      }
    ),
  });
};

type DefaultProps = {
  label: string;
  icon?: React.ReactNode;
  className?: string;
  size?: 'condensed' | 'default';
  color?: 'default' | 'primary' | 'success' | 'danger' | 'warning' | 'danger-dark' | 'accessory2' | 'accessory5';
};

type IconProps = {
  icon: React.ReactNode;
  label?: string;
  size?: 'condensed' | 'default';
  color?: 'default' | 'primary' | 'success' | 'danger' | 'warning' | 'danger-dark' | 'accessory2' | 'accessory5';
};

export type BaseButtonProps = React.ButtonHTMLAttributes<HTMLDivElement> & VariantProps<typeof tagVariants>;

export type TagProps = BaseButtonProps & (IconProps | DefaultProps);

const Tag = ({ className, variant, color, label, icon, size, ...props }: TagProps) => {
  return (
    <div className={cn(tagVariants({ variant, className, color, size }))} {...props}>
      <div className='flex gap-2 leading-100 items-center'>
        {icon ? customIcon(icon, color, variant) : null}
        {label}
      </div>
    </div>
  );
};

Tag.displayName = 'Tag';

export default Tag;
